import React, { CSSProperties, forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PlusIcon, EditPhotoIcon, ErrorNotificationIcon } from '@/assets/icons';
import styles from './uploader.module.scss';
import clsx from 'clsx';
import { Progress, ProgressRedesigned } from '@/ui-kit';
import { showDeleteLastPhotoToast } from '@/utils/show-delete-last-photo-toast';
import { useLocation } from 'react-router-dom';
import { useToast } from '@/store/useToast';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { UploaderStyleTranslationProps } from './uploader-style-translation-props';

interface Props {
    onChange?: (file: File | null) => void;
    onDeletePhoto?: () => void;
    photoUrl?: string | null;
    photoCount?: number;
    isDeleteLoading?: boolean;
    isEditLoading?: boolean;
    isActive?: boolean;
    isUploading?: boolean;
    progress?: number;
    isCanDelete?: boolean | undefined;
    className?: string;
    disabledUploaderIconClassName?: string;
}

export const Uploader = forwardRef<{ click: () => void }, Props>(({
    onChange,
    onDeletePhoto,
    photoUrl,
    photoCount,
    isDeleteLoading,
    isEditLoading,
    isActive,
    isUploading,
    progress,
    isCanDelete = undefined,
    className,
    disabledUploaderIconClassName,
}, ref) => {
    const location = useLocation();
    const [imageSrc, setImageSrc] = useState<string | null>(photoUrl ?? null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    
    const handleUploadClick = () => {
        if (isActive && !imageSrc && fileInputRef.current) {
            if (isIOS) {
                fileInputRef.current?.focus();
                fileInputRef.current?.click();
            } else {
                pickImgSourceDialog
                    .current
                    ?.showModal()
            }
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.currentTarget.files && event.currentTarget.files.length > 0) {
            const file = event.currentTarget.files[0];

            const validFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/heif'];
            const maxSizeInMB = 10;
            const validExtensions = ['heic', 'heif'];
            const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();

            if (!validFormats.includes(file.type) && !(fileExtension && validExtensions.includes(fileExtension))) {
                alert('Неверный формат файла. Загрузите изображение в формате PNG, JPEG, JPG или HEIC.');
                return;
            }

            if (file.size > maxSizeInMB * 1024 * 1024) {
                alert(`Размер файла превышает ${maxSizeInMB} МБ. Пожалуйста, загрузите файл меньшего размера.`);
                return;
            }
            const reader = new FileReader();

            reader.onloadend = () => {
                // const result = reader.result as string
                // setTempUrl(result)
                setImageSrc(reader.result as string);
            };

            reader.readAsDataURL(file);

            onChange?.(file);
        }
    };
    const handleRemoveImage = (e: React.MouseEvent<HTMLDivElement>) => {
        const isLastPhoto = photoCount === 1;
        if (isLastPhoto)
            showDeleteLastPhotoToast();
        if (photoCount && isLastPhoto) return;

        e.stopPropagation();
        setImageSrc(null);
        onChange?.(null);
        onDeletePhoto?.();

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEditImage = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (isIOS) {
            fileInputRef.current?.click();
        } else {
            pickImgSourceDialog
                .current
                ?.showModal()
        }
    };

    const pickImgSourceDialog = useRef<HTMLDialogElement>(null)
    const cameraDialog = useRef<HTMLDialogElement>(null)
    const cameraPlayer = useRef<HTMLVideoElement>(null)
    const showToast = useToast((state) => state.show);

    const [isIOS, setIsIOS] = useState(false)
    
    useEffect(() => {
        // @ts-ignore
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Определяем iOS
        setIsIOS(/iPad|iPhone|iPod/.test(userAgent))
    }, [])

    const { t } = useTranslation();

    // Expose custom methods to parent via ref
    useImperativeHandle(ref, () => {
        return {
            click: () => {
                cameraDialog
                    .current
                    ?.showModal()
                navigator
                    .mediaDevices
                    .getUserMedia({ video: true })
                    .then(stream => {
                        cameraPlayer
                            .current!
                            .srcObject = stream
                        cameraPlayer
                            .current!
                            .play()
                    })
                    .catch(_ => {
                        showToast({
                            icon: <ErrorNotificationIcon />,
                            msg: t('error_try_again')
                        });
                    })
            }
        }
    });

    const uploaderStyleTranslationProps: UploaderStyleTranslationProps = {
        '--first-photo-translation': `'${t('first_photo')}'`
    }

    return (
        <>
            <div
                className={
                    clsx(
                        styles.uploader,
                        className,
                        {
                            [styles.disabled]: (!isActive && !imageSrc),
                            [styles.isUploading]: isUploading,
                            [styles.uploaded]: imageSrc,
                        }
                    )
                }
                style={{
                    backgroundImage: imageSrc ? imageSrc : 'unset',
                    ...uploaderStyleTranslationProps
                }}
                onClick={handleUploadClick}>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    disabled={!isActive}
                    className={styles.filePicker}
                    hidden
                    onChange={handleFileChange} />
                {imageSrc && (
                    <img
                        src={imageSrc}
                        alt={t('Uploaded')}
                        className={styles.uploadedImage} />
                )}

                <div
                    className={
                        clsx(
                            styles.uploaderIcon,
                            imageSrc && styles.active,
                            isUploading && progress && isEditLoading && styles.editLoading,
                            isCanDelete === false && styles.editedPhoto,
                        )
                    }
                    onClick={
                        imageSrc && !isDeleteLoading && isCanDelete ?
                            handleRemoveImage
                        : imageSrc && !isEditLoading && isCanDelete === false ?
                            handleEditImage
                        : undefined
                    }>
                    {
                        (isUploading && progress) ?
                            (
                                location
                                    .pathname
                                    .includes('registrationV2') ||
                                location
                                    .pathname
                                    .includes('registrationV3') ||
                                location
                                    .pathname
                                    .includes('registrationV4') ?
                                    <ProgressRedesigned
                                        withText
                                        progress={progress!} />
                                :
                                    <Progress progress={progress!} />
                            )
                        : isCanDelete === false ?
                            <EditPhotoIcon
                                width={34}
                                height={34} />
                        :
                            <PlusIcon
                                className={
                                    clsx(
                                        !isActive && disabledUploaderIconClassName
                                    )
                                }
                                width={34}
                                height={34} />
                    }
                </div>
            </div>
            {
                createPortal(
                    <dialog
                        ref={pickImgSourceDialog}
                        className={styles.pickImgSourceDialog}
                        onClick={(e) => {
                            if (
                                (e.target as HTMLElement)
                                    .classList
                                    .contains(styles.pickImgSourceDialog)
                            )
                                pickImgSourceDialog
                                    .current
                                    ?.close()
                        }}>
                        <ul className={styles.pickImgSourceDialogContent}>
                            <ul className={styles.pickImgSourceDialogContentSources}>
                                <li
                                    className={styles.pickImgSourceDialogContentSourcesItem}
                                    onClick={() => {
                                        pickImgSourceDialog
                                            .current
                                            ?.close()
                                        cameraDialog
                                            .current
                                            ?.showModal()
                                        navigator
                                            .mediaDevices
                                            .getUserMedia({ video: true })
                                            .then(stream => {
                                                cameraPlayer
                                                    .current!
                                                    .srcObject = stream
                                                cameraPlayer
                                                    .current!
                                                    .play()
                                            })
                                            .catch(_ => {
                                                showToast({
                                                    icon: <ErrorNotificationIcon />,
                                                    msg: t('error_try_again')
                                                });
                                            })
                                    }}>{t('open_camera')}</li>
                                <li
                                    className={styles.pickImgSourceDialogContentSourcesItem}
                                    onClick={() => {
                                        fileInputRef
                                            .current
                                            ?.focus();
                                        fileInputRef
                                            .current
                                            ?.click();
                                        pickImgSourceDialog
                                            .current
                                            ?.close()
                                    }}>{t('pick_from_gallery')}</li>
                            </ul>
                            <li
                                className={styles.pickImgSourceDialogContentCancelAction}
                                onClick={
                                () => 
                                    pickImgSourceDialog
                                        .current
                                        ?.close()
                                }>
                                {t('cancel')}
                            </li>
                        </ul>
                    </dialog>,
                    document.body
                )
            }
            {
                createPortal(
                    <dialog
                        ref={cameraDialog}
                        className={styles.cameraDialog}
                        onClick={(e) => {
                            if (
                                (e.target as HTMLElement)
                                    .classList
                                    .contains(styles.cameraDialog)
                            )
                                cameraDialog
                                    .current
                                    ?.close()
                        }}>
                        <div className={styles.cameraDialogContent}>
                            <video
                                className={styles.cameraDialogPlayer}
                                ref={cameraPlayer} />
                            <button
                                type="button"
                                className={styles.cameraDialogCaptureBtn}
                                onClick={() => {
                                    const video: HTMLVideoElement | null =
                                        cameraPlayer.current;
                                    if (video) {
                                        const canvas = document.createElement('canvas');
                                        canvas.setAttribute('width', `${video!.videoWidth}px`)
                                        canvas.setAttribute('height', `${video!.videoHeight}px`)
                                        const ctx = canvas.getContext('2d');
                                        if (ctx) {
                                            // Draw current video frame onto canvas
                                            ctx
                                                .drawImage(
                                                    video,
                                                    0,
                                                    0,
                                                    video
                                                        .videoWidth,
                                                    video
                                                        .videoHeight
                                                );
                                            // TODO: transfer imageDataUrl to backend
                                            canvas.toBlob(async (blob) => {
                                                const blobToFile = (theBlob: Blob, fileName:string): File => {
                                                    const b: any = theBlob;
                                                    //A Blob() is almost a File() - it's just missing the two properties below which we will add
                                                    b.lastModifiedDate = new Date();
                                                    b.name = fileName;
                                                        
                                                    //Cast to a File() type
                                                    return theBlob as File;
                                                }
                                                if (blob) {
                                                    const reader = new FileReader();

                                                    reader.onloadend = () => {
                                                        setImageSrc(reader.result as string);
                                                    };

                                                    const file = blobToFile(
                                                        blob,
                                                        'my-image.png'
                                                    )
                                                    reader.readAsDataURL(file);
                                                    onChange?.(file)
                                                    cameraDialog
                                                        .current
                                                        ?.close()
                                                }
                                            })
                                        }
                                    }
                                }} />
                        </div>
                    </dialog>,
                    document.body
                )
            }
        </>
    );
});
